import { useState, useMemo, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Ticket, Wallet, Fuel, Sparkles, Bookmark, Trash2, Plus, FolderOpen, LayoutGrid, Table as TableIcon, Trophy } from "lucide-react";
import { fmtCurrency } from "@/lib/format";
import { SectionHeader } from "@/components/PageChrome";

const FIELDS = [
  { key: "entryPerHead", label: "Entry Fee per Head", icon: Ticket, prefix: "$", testId: "calc-input-entry-fee", hint: "Charged for every child and staff member attending" },
  { key: "transport", label: "Transport / Fuel", icon: Fuel, prefix: "$", testId: "calc-input-transport" },
  { key: "otherCosts", label: "Other Estimated Costs", icon: Wallet, prefix: "$", testId: "calc-input-other-costs", hint: "Food, materials, venue, contingency" },
];
const ONCOSTS = 0.11;
const n0 = (x) => parseFloat(x) || 0;
const derive = (v, sessions, staffLines) => {
  const attendance = sessions.reduce((s, l) => s + n0(l.heads), 0);
  const grossIncome = sessions.reduce((s, l) => s + n0(l.heads) * n0(l.charge), 0);
  const staffCount = staffLines.reduce((s, l) => s + n0(l.count), 0);
  const entryFee = n0(v.entryPerHead) * attendance;
  const staffFee = n0(v.entryPerHead) * staffCount;
  const wages = Math.round(staffLines.reduce((s, l) => s + n0(l.count) * n0(l.hours) * n0(l.rate), 0) * (1 + ONCOSTS) * 100) / 100;
  return { attendance, grossIncome, staffCount, entryFee, staffFee, wages };
};
const S = (v) => String(v ?? "");
const fromScenario = (s) => ({
  vals: { entryPerHead: S(s.entryPerHead || (s.attendance ? Math.round((s.entryFee / s.attendance) * 100) / 100 : 0)), transport: S(s.transport), otherCosts: S(s.otherCosts || 0) },
  sessions: s.sessions?.length ? s.sessions.map((l) => ({ label: l.label, heads: S(l.heads), charge: S(l.charge) })) : [{ label: "All sessions", heads: S(s.attendance), charge: S(s.charge) }],
  staffLines: s.staffLines?.length ? s.staffLines.map((l) => ({ role: l.role, count: S(l.count), hours: S(l.hours), rate: S(l.rate) })) : [{ role: "Staff", count: S(s.staffCount || 0), hours: S(s.staffHours || 0), rate: S(s.staffRate || 0) }],
});

const PRESETS = [
  { name: "Community Event", vals: { entryPerHead: 8, transport: 120, otherCosts: 150 }, sessions: [{ label: "Morning", heads: 30, charge: 25 }, { label: "Afternoon", heads: 20, charge: 20 }], staffLines: [{ role: "Leader", count: 1, hours: 5, rate: 32 }, { role: "Assistants", count: 4, hours: 4, rate: 26 }] },
  { name: "Corporate Workshop", vals: { entryPerHead: 56, transport: 195.97, otherCosts: 0 }, sessions: [{ label: "Full day", heads: 20, charge: 120 }], staffLines: [{ role: "Facilitators", count: 2, hours: 8, rate: 30 }] },
  { name: "Outdoor Adventure Camp", vals: { entryPerHead: 9, transport: 480, otherCosts: 600 }, sessions: [{ label: "Day trip", heads: 60, charge: 45 }, { label: "Half day", heads: 40, charge: 30 }], staffLines: [{ role: "Leaders", count: 2, hours: 8, rate: 34 }, { role: "Assistants", count: 8, hours: 6, rate: 26 }] },
];

function LineEditor({ title, rows, setRows, cols, prefix, addLabel, blank }) {
  const set = (i, k, v) => setRows(rows.map((r, idx) => (idx === i ? { ...r, [k]: v } : r)));
  return (
    <div className="rounded-xl border border-[var(--hp-border)] bg-[var(--hp-bg)]/40 p-3.5 space-y-2" data-testid={`${prefix}-editor`}>
      <p className="text-xs font-semibold uppercase tracking-wider text-[var(--hp-muted)]">{title}</p>
      {rows.map((row, i) => (
        <div key={i} className="grid grid-cols-2 items-center gap-2 border-b border-[var(--hp-border)]/50 pb-2 last:border-0 last:pb-0 sm:grid-cols-none sm:border-0 sm:pb-0 sm:[grid-template-columns:var(--cols)]" style={{ "--cols": `1.6fr ${cols.slice(1).map(() => "1fr").join(" ")} auto` }} data-testid={`${prefix}-row-${i}`}>
          {cols.map((c, ci) => (
            <div key={c.key} className={`relative ${ci === 0 ? "col-span-2 sm:col-span-1" : ""}`}>{c.money && <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[var(--hp-muted)] font-mono-num text-xs">$</span>}
              <input data-testid={`${prefix}-${c.key}-${i}`} type={c.text ? "text" : "number"} min="0" step="any" value={row[c.key]} onChange={(e) => set(i, c.key, e.target.value)} placeholder={c.label}
                className={`w-full rounded-lg border border-[var(--hp-border-hi)] bg-[var(--hp-bg)] py-1.5 ${c.money ? "pl-6" : "px-2.5"} pr-2 text-xs text-[var(--hp-heading)] outline-none focus:border-[var(--hp-primary)] ${c.text ? "" : "font-mono-num"}`} /></div>
          ))}
          <button type="button" data-testid={`${prefix}-remove-${i}`} disabled={rows.length === 1} onClick={() => setRows(rows.filter((_, idx) => idx !== i))} className="justify-self-end text-[var(--hp-muted)] hover:text-[var(--hp-loss)] disabled:opacity-30"><Trash2 className="h-3.5 w-3.5" /></button>
        </div>
      ))}
      <button type="button" data-testid={`${prefix}-add`} onClick={() => setRows([...rows, { ...blank }])} className="inline-flex items-center gap-1 rounded-full border border-[var(--hp-border-hi)] px-2.5 py-1 text-xs font-semibold text-[var(--hp-heading)] hover:bg-[var(--hp-tint-5)]"><Plus className="h-3 w-3" /> {addLabel}</button>
    </div>
  );
}

export default function Calculator({ api }) {
  const [vals, setVals] = useState({ entryPerHead: "56", transport: "195.97", otherCosts: "0" });
  const [sessions, setSessions] = useState([{ label: "Full day", heads: "20", charge: "95" }]);
  const [staffLines, setStaffLines] = useState([{ role: "Facilitators", count: "2", hours: "8", rate: "30" }]);
  const [scenarios, setScenarios] = useState([]);
  const [scenarioName, setScenarioName] = useState("");
  const [savingScenario, setSavingScenario] = useState(false);
  const [scenarioView, setScenarioView] = useState("cards"); // cards | table

  const set = (k, v) => setVals((s) => ({ ...s, [k]: v }));
  const num = (k) => parseFloat(vals[k]) || 0;

  const loadScenarios = useCallback(async () => {
    try {
      const res = await axios.get(`${api}/scenarios`);
      setScenarios(res.data);
    } catch (e) { console.error(e); }
  }, [api]);

  useEffect(() => { loadScenarios(); }, [loadScenarios]);

  const r = useMemo(() => {
    const { attendance, grossIncome, staffCount, entryFee, staffFee, wages } = derive(vals, sessions, staffLines);
    const totalCosts = entryFee + staffFee + wages + num("transport") + num("otherCosts");
    const netProfit = grossIncome - totalCosts;
    const costPerHead = attendance > 0 ? totalCosts / attendance : 0;
    const recPrice = costPerHead * 1.15;
    const ratio = staffCount > 0 ? attendance / staffCount : null;
    return { attendance, grossIncome, staffCount, totalCosts, netProfit, costPerHead, recPrice, entryFee, staffFee, wages, ratio, avgCharge: attendance ? grossIncome / attendance : 0 };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vals, sessions, staffLines]);

  const positive = r.netProfit >= 0;

  const applyPreset = (p) => { setVals(Object.fromEntries(Object.entries(p.vals).map(([k, val]) => [k, String(val)]))); setSessions(p.sessions.map((l) => ({ label: l.label, heads: S(l.heads), charge: S(l.charge) }))); setStaffLines(p.staffLines.map((l) => ({ role: l.role, count: S(l.count), hours: S(l.hours), rate: S(l.rate) }))); };

  const saveScenario = async () => {
    if (!scenarioName.trim()) { toast.error("Give the scenario a name"); return; }
    setSavingScenario(true);
    try {
      await axios.post(`${api}/scenarios`, {
        name: scenarioName.trim(),
        attendance: r.attendance, charge: Math.round(r.avgCharge * 100) / 100, entryFee: r.entryFee,
        staffFee: r.staffFee, wages: r.wages, transport: num("transport"), otherCosts: num("otherCosts"),
        entryPerHead: num("entryPerHead"), staffCount: r.staffCount, staffHours: n0(staffLines[0]?.hours), staffRate: n0(staffLines[0]?.rate),
        sessions: sessions.map((l) => ({ label: l.label, heads: n0(l.heads), charge: n0(l.charge) })),
        staffLines: staffLines.map((l) => ({ role: l.role, count: n0(l.count), hours: n0(l.hours), rate: n0(l.rate) })),
      });
      setScenarioName("");
      toast.success("Scenario saved");
      await loadScenarios();
    } catch (e) {
      toast.error("Failed to save scenario");
    } finally {
      setSavingScenario(false);
    }
  };

  const loadScenario = (s) => {
    applyPreset(fromScenario(s));
    toast.success(`Loaded "${s.name}"`);
  };

  const deleteScenario = async (id) => {
    try {
      await axios.delete(`${api}/scenarios/${id}`);
      await loadScenarios();
      toast.success("Scenario deleted");
    } catch (e) { toast.error("Failed to delete"); }
  };

  const scenarioMetrics = (s) => {
    const gross = s.attendance * s.charge;
    const costs = s.entryFee + s.staffFee + s.wages + s.transport;
    const net = gross - costs;
    const costPerHead = s.attendance > 0 ? costs / s.attendance : 0;
    const recPrice = costPerHead * 1.15;
    return { gross, costs, net, costPerHead, recPrice };
  };

  const bestId = scenarios.length
    ? scenarios.reduce((best, s) => (scenarioMetrics(s).net > scenarioMetrics(best).net ? s : best)).id
    : null;

  const results = [
    { label: "Gross Income", value: fmtCurrency(r.grossIncome), testId: "calc-output-gross-income" },
    { label: "Total Costs", value: fmtCurrency(r.totalCosts), testId: "calc-output-total-costs" },
  ];

  return (
    <div className="space-y-8">
      <SectionHeader title="Activity margin & pricing calculator" description="Live event profitability — updates as you type." />

      {/* Presets */}
      <div className="flex flex-wrap gap-2.5">
        {PRESETS.map((p) => (
          <button key={p.name} data-testid={`calc-preset-${p.name.toLowerCase().replace(/\s+/g, "-")}`}
            onClick={() => applyPreset(p)}
            className="inline-flex items-center gap-2 rounded-full border border-[var(--hp-border-hi)] bg-[var(--hp-card)] px-4 py-2 text-xs font-semibold text-[var(--hp-text)] hover:bg-[var(--hp-card-hover)] hover:text-[var(--hp-heading)] transition-colors duration-200">
            <Sparkles className="h-3.5 w-3.5 text-[var(--hp-gold)]" /> {p.name}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Inputs */}
        <div className="lg:col-span-7 rounded-2xl border border-[var(--hp-border)] bg-[var(--hp-card)] p-6">
          <h3 className="font-head text-lg font-semibold text-[var(--hp-heading)] mb-5">Event Inputs</h3>
          <div className="space-y-4 mb-5">
            <LineEditor title="Sessions & charges per head" prefix="calc-session" rows={sessions} setRows={setSessions} addLabel="Add session time" blank={{ label: "", heads: "", charge: "" }}
              cols={[{ key: "label", label: "Session (e.g. Morning 9–12)", text: true }, { key: "heads", label: "Heads" }, { key: "charge", label: "Charge/head", money: true }]} />
            <LineEditor title="Staff attending" prefix="calc-staff" rows={staffLines} setRows={setStaffLines} addLabel="Add staff group" blank={{ role: "", count: "", hours: "", rate: "" }}
              cols={[{ key: "role", label: "Role", text: true }, { key: "count", label: "How many" }, { key: "hours", label: "Hours each" }, { key: "rate", label: "Rate/hr", money: true }]} />
          </div>
          <div className="grid gap-5 grid-cols-[repeat(auto-fit,minmax(11rem,1fr))]">
            {FIELDS.map((f) => {
              const Icon = f.icon;
              return (
                <div key={f.key}>
                  <label className="text-xs font-semibold uppercase tracking-wider text-[var(--hp-muted)] flex flex-wrap items-center gap-x-1.5 gap-y-0.5 mb-2">
                    <span className="inline-flex items-center gap-1.5"><Icon className="h-3.5 w-3.5 shrink-0" /> {f.label}</span>{f.hint && <span className="basis-full normal-case tracking-normal font-normal text-xs">{f.hint}</span>}
                  </label>
                  <div className="relative">
                    {f.prefix && (
                      <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[var(--hp-muted)] font-mono-num text-sm">{f.prefix}</span>
                    )}
                    <input
                      data-testid={f.testId}
                      type="number"
                      value={vals[f.key]}
                      onChange={(e) => set(f.key, e.target.value)}
                      className={`w-full rounded-xl border border-[var(--hp-border-hi)] bg-[var(--hp-bg)] py-3 ${f.prefix ? "pl-7" : "pl-3.5"} pr-3.5 font-mono-num text-[var(--hp-heading)] outline-none focus:border-[var(--hp-primary)] focus:ring-2 focus:ring-[var(--hp-primary)]/30 transition-colors duration-200`}
                    />
                  </div>
                </div>
              );
            })}
          </div>
          <div className="mt-4 grid gap-3 grid-cols-[repeat(auto-fit,minmax(7.5rem,1fr))] rounded-xl border border-[var(--hp-border)] bg-[var(--hp-bg)]/40 p-3.5 text-xs" data-testid="calc-derived">
            <div><p className="text-xs uppercase tracking-wider text-[var(--hp-muted)]">Attendance</p><p className="font-mono-num text-[var(--hp-heading)] [overflow-wrap:anywhere]" data-testid="calc-derived-attendance">{r.attendance} @ avg {fmtCurrency(r.avgCharge)}</p></div>
            <div><p className="text-xs uppercase tracking-wider text-[var(--hp-muted)]">Kids' entry</p><p className="font-mono-num text-[var(--hp-heading)] [overflow-wrap:anywhere]" data-testid="calc-derived-entry">{fmtCurrency(r.entryFee)}</p></div>
            <div><p className="text-xs uppercase tracking-wider text-[var(--hp-muted)]">Staff entry</p><p className="font-mono-num text-[var(--hp-heading)] [overflow-wrap:anywhere]" data-testid="calc-derived-staff-entry">{fmtCurrency(r.staffFee)}</p></div>
            <div><p className="text-xs uppercase tracking-wider text-[var(--hp-muted)]">Wages incl. on-costs</p><p className="font-mono-num text-[var(--hp-heading)] [overflow-wrap:anywhere]" data-testid="calc-derived-wages">{fmtCurrency(r.wages)}</p></div>
            <div><p className="text-xs uppercase tracking-wider text-[var(--hp-muted)]">Ratio ({r.staffCount} staff)</p><p className={`font-mono-num ${r.ratio && r.ratio > 10 ? "text-[var(--hp-gold)]" : "text-[var(--hp-heading)]"}`} data-testid="calc-derived-ratio">{r.ratio ? `1:${Math.round(r.ratio * 10) / 10}` : "—"}</p></div>
          </div>
          <div className="mt-6 pt-5 border-t border-[var(--hp-border)]">
            <label className="text-xs font-semibold uppercase tracking-wider text-[var(--hp-muted)] mb-2 block">Save this setup as a scenario</label>
            <div className="flex flex-wrap items-center gap-3">
              <input data-testid="scenario-name-input" value={scenarioName} onChange={(e) => setScenarioName(e.target.value)}
                placeholder="e.g. School Gala Quote"
                className="min-w-0 flex-1 rounded-xl border border-[var(--hp-border-hi)] bg-[var(--hp-bg)] py-2.5 px-3.5 text-sm text-[var(--hp-heading)] outline-none focus:border-[var(--hp-primary)] transition-colors duration-200" />
              <button data-testid="save-scenario-button" onClick={saveScenario} disabled={savingScenario}
                className="inline-flex items-center gap-2 rounded-full bg-[var(--hp-primary)] px-5 py-2.5 text-sm font-bold text-[#04231a] hover:bg-[var(--hp-primary-hover)] transition-colors duration-200 disabled:opacity-50">
                <Bookmark className="h-4 w-4" /> Save
              </button>
            </div>
          </div>
        </div>

        {/* Results */}
        <div className="lg:col-span-5 space-y-4">
          <div className="grid gap-4 grid-cols-[repeat(auto-fit,minmax(9.5rem,1fr))]">
            {results.map((res) => (
              <div key={res.label} data-testid={res.testId}
                className="rounded-2xl border border-[var(--hp-border)] bg-[var(--hp-card)] p-5">
                <p className="text-xs font-semibold uppercase tracking-wider text-[var(--hp-muted)]">{res.label}</p>
                <p className="font-mono-num text-xl font-bold text-[var(--hp-heading)] mt-2 [overflow-wrap:anywhere]">{res.value}</p>
              </div>
            ))}
          </div>

          <div className="rounded-2xl border border-[var(--hp-border)] bg-[var(--hp-card)] p-5" data-testid="calc-output-cost-per-head">
            <p className="text-xs font-semibold uppercase tracking-wider text-[var(--hp-muted)]">Cost Per Head</p>
            <p className="font-mono-num text-xl font-bold text-[var(--hp-heading)] mt-2">{fmtCurrency(r.costPerHead)}</p>
          </div>

          {/* Net profit hero */}
          <motion.div
            animate={{ borderColor: positive ? "rgba(16,185,129,0.4)" : "rgba(239,68,68,0.4)" }}
            data-testid="calc-output-net-profit"
            className="rounded-2xl border p-6 bg-[var(--hp-card)]"
            style={{ background: positive ? "rgba(16,185,129,0.08)" : "rgba(239,68,68,0.08)" }}>
            <p className="text-xs font-semibold uppercase tracking-wider text-[var(--hp-muted)]">Net Profit</p>
            <p className={`font-mono-num text-4xl font-extrabold mt-2 ${positive ? "text-[var(--hp-profit)]" : "text-[var(--hp-loss)]"}`}>
              {positive ? "+" : ""}{fmtCurrency(r.netProfit)}
            </p>
            <p className="text-xs text-[var(--hp-muted)] mt-2">
              {positive ? "This event is projected to be profitable." : "This event is projected to run at a loss."}
            </p>
          </motion.div>

          {/* Recommended price */}
          <div data-testid="calc-output-recommended-price"
            className="rounded-2xl border border-[var(--hp-gold)]/40 p-6" style={{ background: "rgba(245,158,11,0.08)" }}>
            <p className="text-xs font-semibold uppercase tracking-wider text-[var(--hp-muted)]">Recommended Price / Head (+15% margin)</p>
            <p className="font-mono-num text-3xl font-bold text-[var(--hp-gold)] mt-2">{fmtCurrency(r.recPrice)}</p>
          </div>
        </div>
      </div>

      {/* Saved scenarios */}
      <div className="rounded-2xl border border-[var(--hp-border)] bg-[var(--hp-card)] overflow-hidden" data-testid="saved-scenarios-section">
        <div className="px-6 py-4 border-b border-[var(--hp-border)] flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <FolderOpen className="h-5 w-5 text-[var(--hp-profit)]" />
            <h3 className="font-head text-lg font-semibold text-[var(--hp-heading)]">Saved Scenarios</h3>
          </div>
          {scenarios.length > 0 && (
            <div className="flex rounded-lg border border-[var(--hp-border-hi)] overflow-hidden">
              <button data-testid="scenario-view-cards" onClick={() => setScenarioView("cards")}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold transition-colors duration-200 ${
                  scenarioView === "cards" ? "bg-[var(--hp-primary)] text-[#04231a]" : "bg-[var(--hp-bg)] text-[var(--hp-muted)] hover:text-[var(--hp-heading)]"}`}>
                <LayoutGrid className="h-3.5 w-3.5" /> Cards
              </button>
              <button data-testid="scenario-view-table" onClick={() => setScenarioView("table")}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold transition-colors duration-200 ${
                  scenarioView === "table" ? "bg-[var(--hp-primary)] text-[#04231a]" : "bg-[var(--hp-bg)] text-[var(--hp-muted)] hover:text-[var(--hp-heading)]"}`}>
                <TableIcon className="h-3.5 w-3.5" /> Compare
              </button>
            </div>
          )}
        </div>

        {scenarios.length === 0 && (
          <p className="px-6 py-8 text-center text-sm text-[var(--hp-muted)]">No saved scenarios yet. Configure inputs above and hit Save.</p>
        )}

        {scenarios.length > 0 && scenarioView === "cards" && (
          <div className="p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {scenarios.map((s) => {
              const { net } = scenarioMetrics(s);
              return (
                <div key={s.id} data-testid={`scenario-card-${s.id}`}
                  className={`group rounded-xl border bg-[var(--hp-bg)] p-4 transition-colors duration-200 ${
                    s.id === bestId ? "border-[var(--hp-primary)]/60" : "border-[var(--hp-border)] hover:border-[var(--hp-border-hi)]"}`}>
                  <div className="flex items-start justify-between gap-2">
                    <p className="text-sm font-semibold text-[var(--hp-heading)] truncate flex items-center gap-1.5">
                      {s.id === bestId && <Trophy className="h-3.5 w-3.5 text-[var(--hp-gold)]" />}{s.name}
                    </p>
                    <button data-testid={`delete-scenario-${s.id}`} onClick={() => deleteScenario(s.id)}
                      className="grid place-items-center h-7 w-7 rounded-lg text-[var(--hp-muted)] hover:text-[var(--hp-loss)] hover:bg-[var(--hp-loss)]/10 transition-colors duration-150">
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                  <p className="text-xs text-[var(--hp-muted)] mt-1">{s.attendance} heads · {fmtCurrency(s.charge)}/head</p>
                  <p className={`font-mono-num text-lg font-bold mt-2 ${net >= 0 ? "text-[var(--hp-profit)]" : "text-[var(--hp-loss)]"}`}>{net >= 0 ? "+" : ""}{fmtCurrency(net)}</p>
                  <button data-testid={`load-scenario-${s.id}`} onClick={() => loadScenario(s)}
                    className="mt-3 w-full rounded-full border border-[var(--hp-border-hi)] bg-[var(--hp-card)] py-2 text-xs font-semibold text-[var(--hp-heading)] hover:bg-[var(--hp-card-hover)] transition-colors duration-200">
                    Load into calculator
                  </button>
                </div>
              );
            })}
          </div>
        )}

        {scenarios.length > 0 && scenarioView === "table" && (
          <div className="overflow-x-auto">
            <table data-testid="scenario-compare-table" className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wider text-[var(--hp-muted)] border-b border-[var(--hp-border)]">
                  <th className="px-5 py-3 font-semibold">Scenario</th>
                  <th className="px-5 py-3 font-semibold text-right">Heads</th>
                  <th className="px-5 py-3 font-semibold text-right">Charge</th>
                  <th className="px-5 py-3 font-semibold text-right">Gross</th>
                  <th className="px-5 py-3 font-semibold text-right">Costs</th>
                  <th className="px-5 py-3 font-semibold text-right">Net Profit</th>
                  <th className="px-5 py-3 font-semibold text-right">Cost / Head</th>
                  <th className="px-5 py-3 font-semibold text-right">Rec. Price</th>
                  <th className="px-5 py-3 font-semibold text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {scenarios.map((s) => {
                  const m = scenarioMetrics(s);
                  const isBest = s.id === bestId;
                  return (
                    <tr key={s.id} data-testid={`scenario-compare-row-${s.id}`}
                      className={`border-b border-[var(--hp-border)]/60 transition-colors duration-150 ${isBest ? "bg-[var(--hp-primary)]/[0.06]" : "hover:bg-[var(--hp-tint-3)]"}`}>
                      <td className="px-5 py-3.5 font-semibold text-[var(--hp-heading)]">
                        <span className="flex items-center gap-1.5">
                          {isBest && <Trophy className="h-3.5 w-3.5 text-[var(--hp-gold)]" />}{s.name}
                        </span>
                      </td>
                      <td className="px-5 py-3.5 text-right font-mono-num text-[var(--hp-text)]">{s.attendance}</td>
                      <td className="px-5 py-3.5 text-right font-mono-num text-[var(--hp-text)]">{fmtCurrency(s.charge)}</td>
                      <td className="px-5 py-3.5 text-right font-mono-num text-[var(--hp-text)]">{fmtCurrency(m.gross)}</td>
                      <td className="px-5 py-3.5 text-right font-mono-num text-[var(--hp-text)]">{fmtCurrency(m.costs)}</td>
                      <td className={`px-5 py-3.5 text-right font-mono-num font-bold ${m.net >= 0 ? "text-[var(--hp-profit)]" : "text-[var(--hp-loss)]"}`}>{fmtCurrency(m.net)}</td>
                      <td className="px-5 py-3.5 text-right font-mono-num text-[var(--hp-text)]">{fmtCurrency(m.costPerHead)}</td>
                      <td className="px-5 py-3.5 text-right font-mono-num text-[var(--hp-gold)]">{fmtCurrency(m.recPrice)}</td>
                      <td className="px-5 py-3.5">
                        <div className="flex items-center justify-end gap-1.5">
                          <button data-testid={`table-load-scenario-${s.id}`} onClick={() => loadScenario(s)}
                            className="rounded-full border border-[var(--hp-border-hi)] bg-[var(--hp-card)] px-3 py-1.5 text-xs font-semibold text-[var(--hp-heading)] hover:bg-[var(--hp-card-hover)] transition-colors duration-200">
                            Load
                          </button>
                          <button data-testid={`table-delete-scenario-${s.id}`} onClick={() => deleteScenario(s.id)}
                            className="grid place-items-center h-7 w-7 rounded-lg text-[var(--hp-muted)] hover:text-[var(--hp-loss)] hover:bg-[var(--hp-loss)]/10 transition-colors duration-150">
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
